// Bulk Upload v2 file transport (build doc 06 D15, §4.2; 90 #38, #39).
//
// Files go through the v1 handwritten route, so each one becomes an
// `uploadedFiles` row and the commit's PageRef(fileUrl, pageIndex) stays
// byte-identical to v1's. What v2 needs back is that row's id, which the
// ingestion job is told about with POST /ingestion/{jobId}/files/register:
// - Word documents, and PDFs/images up to Cloud Run's 32 MiB request cap, take
//   POST /files/upload exactly as v1 does (server-side Word → PDF and PDF
//   compression), asking for the JSON identity with `Accept: application/json`;
// - larger PDFs/images (or a failed small upload) go direct to storage:
//   /uploads/handwritten/sign → signed PUT → /uploads/handwritten/complete,
//   whose reply is the same identity.
// HEIC/HEIF photos are converted to JPEG in the browser first: ingestion
// refuses image/heic for every owner kind (90 #39).
import {
  ExamsApiError,
  endExpiredSession,
  errorFromResponse,
  examsApiBase,
  parseBody,
} from '../../../service/exams.api';
import { convertHeicToJpeg } from '../../../service/exams.upload';
import { getAuthToken } from '../../../userLocalDetails';

export const LEGACY_UPLOAD_MAX_BYTES = 32 * 1024 * 1024;

const isHeic = (file) =>
  /\.(heic|heif)$/i.test(file?.name || '') ||
  /^image\/hei[cf]$/i.test(file?.type || '');

const isWord = (file) => /\.docx$/i.test(file?.name || '');

const authHeader = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const send = async (url, init) => {
  let response;
  try {
    response = await fetch(url, { credentials: 'include', ...init });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ExamsApiError(error?.message || 'Network error', {
      code: 'NETWORK',
    });
  }
  if (response.status === 401) {
    endExpiredSession();
    throw new ExamsApiError('Your session has expired.', {
      status: 401,
      code: 'UNAUTHENTICATED',
    });
  }
  const body = await parseBody(response);
  if (!response.ok) throw errorFromResponse(response, body);
  return body;
};

const jsonPost = (path, body, signal) =>
  send(`${examsApiBase()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(body),
    signal,
  });

/** The uploadedFiles identity {fileId, fileUrl, contentType, sizeBytes, pageCount}. */
const identityOf = (body) => {
  if (!body?.fileId || !body?.fileUrl) {
    throw new ExamsApiError("The upload didn't finish. Try again.", {
      code: 'UPLOAD_INCOMPLETE',
    });
  }
  return body;
};

const uploadThroughApi = async (file, signal) => {
  const form = new FormData();
  form.append('file', file);
  return identityOf(
    await send(`${examsApiBase()}/files/upload`, {
      method: 'POST',
      headers: { Accept: 'application/json', ...authHeader() },
      body: form,
      signal,
    })
  );
};

const uploadDirect = async (file, signal) => {
  const signed = await jsonPost(
    '/uploads/handwritten/sign',
    {
      fileName: file.name,
      contentType: file.type || '',
      fileSizeBytes: file.size,
    },
    signal
  );
  if (!signed?.uploadUrl || !signed?.accessUrl) {
    throw new ExamsApiError("The upload couldn't start. Try again.", {
      code: 'UPLOAD_NOT_SIGNED',
    });
  }
  let put;
  try {
    put = await fetch(signed.uploadUrl, {
      method: 'PUT',
      headers: signed.requiredHeaders || {},
      body: file,
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ExamsApiError("The file couldn't reach storage. Try again.", {
      code: 'NETWORK',
    });
  }
  if (!put.ok) {
    throw new ExamsApiError("The file couldn't be stored. Try again.", {
      status: put.status,
      code: 'UPLOAD_FAILED',
    });
  }
  return identityOf(
    await jsonPost(
      '/uploads/handwritten/complete',
      {
        accessUrl: signed.accessUrl,
        fileName: file.name,
        contentType: file.type || '',
        sizeBytes: file.size,
      },
      signal
    )
  );
};

/**
 * Upload one bulk file. Resolves {uploadedFileId, fileUrl, name, pageCount};
 * throws ExamsApiError (or an AbortError when `signal` aborts).
 */
export const uploadBulkFile = async (original, { signal } = {}) => {
  const file = isHeic(original) ? await convertHeicToJpeg(original) : original;
  let identity;
  if (isWord(file)) {
    identity = await uploadThroughApi(file, signal);
  } else if (file.size <= LEGACY_UPLOAD_MAX_BYTES) {
    try {
      identity = await uploadThroughApi(file, signal);
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 'UNAUTHENTICATED')
        throw error;
      identity = await uploadDirect(file, signal);
    }
  } else {
    identity = await uploadDirect(file, signal);
  }
  return {
    uploadedFileId: identity.fileId,
    fileUrl: identity.fileUrl,
    name: original.name,
    pageCount: identity.pageCount ?? null,
  };
};
